import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useQueryCacheManager } from "@/hooks/useQueryCacheManager";
import { supabase } from "@/integrations/supabase/client";
import { logger } from '@/lib/logger';

/**
 * Unified logout hook that ensures consistent sign-out behavior
 * across the entire application. Handles:
 * - Global sign-out with token revocation
 * - Cache clearing to prevent data leakage
 * - Local storage cleanup
 * - Auth state propagation
 * - Navigation with proper redirects
 */
export function useLogout() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clearAllCaches } = useQueryCacheManager();

  const logout = async (redirectTo: string = "/") => {
    try {
      console.debug('[useLogout] Starting logout process');
      
      // Sign out with global scope to revoke refresh tokens across all tabs/sessions
      const { error } = await supabase.auth.signOut({ scope: "global" });
      
      if (error) {
        logger.error('[useLogout] Logout error:', error);
        toast({
          title: "Logout failed",
          description: "Please try again or refresh the page",
          variant: "destructive",
        });
        return;
      }
      
      console.debug('[useLogout] Sign-out successful, clearing caches');
      
      // Clear all React Query caches AFTER successful sign-out
      clearAllCaches();
      
      // Defensive cleanup: remove auth key from localStorage
      // (Supabase should handle this, but we're being extra safe)
      try {
        localStorage.removeItem("of-auth");
      } catch (e) {
        console.warn('[useLogout] Failed to clear localStorage:', e);
      }
      
      // Wait for auth state to propagate through React
      await new Promise(resolve => setTimeout(resolve, 150));
      
      console.debug('[useLogout] Navigating to:', redirectTo);
      
      // Navigate to the target page
      navigate(redirectTo, { replace: true });
      
    } catch (error) {
      logger.error('[useLogout] Logout exception:', error);
      toast({
        title: "Unexpected logout error",
        description: "Refresh the page to clear your session",
        variant: "destructive",
      });
    }
  };

  return { logout };
}
